import { Component, computed, effect, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { historyBack } from '../../utils'
import { NotificationService } from '../../services'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { TranslateService } from '@ngx-translate/core'
import { DepotService } from '../../services/entity-services/depot.service'
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental'
import { updateCachedListQueries } from '../../services/query-cache.service'
import { Depot } from 'depoto-core/src/entities'
import { PayloadBuilderService } from '../../services/payload-builder.service'

const depotSchema = {
  id: null,
  name: null,
  emailIn: null,
  unavailablesUrl: null,
  company: {
    id: null,
    name: null,
  },
  enabled: null,
  deletable: null,
  position: null,
  institution: {
    id: null,
    name: null,
  },
  checkouts: {
    id: null,
    name: null,
  },
  users: {
    id: null,
    name: null,
  },
}

@Component({
  selector: 'app-depot',
  templateUrl: 'depot.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DepotComponent implements CheckDataChanges, OnDestroy {
  private depotService = inject(DepotService)
  private queryClient = inject(QueryClient)
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private notificationService = inject(NotificationService)
  private translateService = inject(TranslateService)
  private payloadBuilder = inject(PayloadBuilderService)

  // Overridden in the constructor below and restored in ngOnDestroy - see there for why.
  private previousShouldReuseRoute: Router['routeReuseStrategy']['shouldReuseRoute']

  submitted = signal(false)

  private initialFormValue = signal<any>(null)

  depotForm = new FormGroup({
    name: new FormControl({ value: '', disabled: false }, [Validators.required]),
    emailIn: new FormControl({ value: '', disabled: false }, []),
  })

  depotId = computed(() => Number(this.route.snapshot.params['id']))

  // Expose depot data as a computed value (ensure we call the getter)
  depot = computed(() => this.queryDepot.data())

  get formControls(): { [key: string]: AbstractControl } {
    return this.depotForm.controls
  }

  readonly queryDepot = injectQuery(() => ({
    queryKey: ['depot', this.route.snapshot.params['id']],
    queryFn: async () => {
      const id = this.route.snapshot.params['id']
      if (id) {
        return await this.depotService.getDepot(id, depotSchema).then(result => {
          this.fillFormValue(result)
          return result
        })
      } else {
        return new Depot()
      }
    },
  }))

  createDepotMutation = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      if (this.depotForm.invalid) {
        throw new Error('Form is invalid')
      }

      const current = this.depotForm.getRawValue() as any
      const initial = this.initialFormValue() as any
      const n = this.payloadBuilder.buildPatch(current, initial, {
        // no id on create
        required: ['name', 'emailIn'],
        optional: {
          name: {},
          emailIn: {},
        },
      })

      // Return the created Depot so onSuccess receives the entity with id
      return await this.depotService.createDepot(n, { id: null } as any)
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.depotForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.depotForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('alert.depot.created'))
      // Update detail cache for this depot
      this.queryClient.setQueryData(['depot', data.id], data)
      // OPTIONS: update options/list caches so selects/filters see the new depot immediately
      updateCachedListQueries(this.queryClient, ['depots', 'options'], data, {
        mode: 'insert',
        insertPosition: 'start',
      })

      this.removeDepotListings()

      // Invalidate all depot-related caches by prefix so selects and listings refresh
      // this.queryClient.invalidateQueries({ queryKey: ['depots'] })
      this.router.navigate(['/depot', data['id']], { replaceUrl: true })
    },
  }))

  updateDepotMutation = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      this.getDirtyValues()
      const current = this.depotForm.getRawValue() as any
      const initial = this.initialFormValue() as any
      const id = Number(this.router.url.split('/')[2] || this.route.snapshot.params['id'])
      const patch = this.payloadBuilder.buildPatch(current, initial, {
        id,
        required: ['name', 'emailIn'],
        optional: {
          name: {},
          emailIn: {},
        },
      })
      return await this.depotService.updateDepot(patch as any, depotSchema as any)
    },
    onSuccess: data => {
      this.submitted.set(false)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.depotForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.depotForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('alert.depot.updated'))
      // Update detail cache for this depot
      this.queryClient.setQueryData(['depot', data['id']], data)
      // OPTIONS: update options cache value for this depot
      updateCachedListQueries(this.queryClient, ['depots', 'options'], data, { mode: 'update' })

      this.removeDepotListings()

      this.router.navigate(['/depot', data['id']])
    },
    enabled: !!this.router.url.split('/')[2],
  }))

  deleteDepotMutation = injectMutation(() => ({
    mutationFn: async () => {
      this.getDirtyValues()
      await this.depotService.deleteDepot({ id: this.route.snapshot.params['id'] }).then(result => {
        this.notificationService.success(this.translateService.instant('alert.depot.deleted'))
        // Prune deleted depot from any cached lists (supports infinite queries and arrays)
        const idStr = String(this.route.snapshot.params['id'])
        updateCachedListQueries(this.queryClient, ['depots', 'options'], { id: Number(idStr) } as any, {
          mode: 'delete',
        })
        // Remove detail cache for this depot
        this.queryClient.removeQueries({ queryKey: ['depot', Number(idStr)], exact: true })
        this.router.navigate(['/depots'], { replaceUrl: true })
      })
    },
    enabled: !!this.router.url.split('/')[2],
  }))

  isCreating = computed(() => !this.depotId())

  isLoading = computed(() => {
    return this.queryDepot.isPending() || this.queryDepot.isFetching()
  })

  constructor() {
    effect(() => {
      const d = this.queryDepot.data()
      const id = this.route.snapshot.params['id']
      if (id && d) {
        this.fillFormValue(d)
      }
    })
    // Forces this route to reload fresh (rather than being reused) when navigating between
    // different depot ids. This mutates the app-wide Router's routeReuseStrategy though, not
    // something scoped to this component - left in place it silently breaks route reuse (e.g.
    // wizard-style child routes elsewhere, like order creation) for the rest of the session. Put
    // the original back on the way out.
    this.previousShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute.bind(this.router.routeReuseStrategy)
    this.router.routeReuseStrategy.shouldReuseRoute = () => false
  }

  ngOnDestroy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = this.previousShouldReuseRoute
  }

  hasUnsavedDataChanges(): boolean {
    // todo
    return false
  }

  goBack(): void {
    historyBack()
  }

  getDirtyValues(form = this.depotForm) {
    const dirtyValues = {}

    Object.keys(form.controls).forEach(key => {
      const currentControl = form.controls[key]

      if (currentControl.dirty) {
        if (currentControl.controls) dirtyValues[key] = this.getDirtyValues(currentControl)
        else dirtyValues[key] = currentControl.value
      }
    })

    return dirtyValues
  }

  getDataToSave() {
    const current = this.depotForm.getRawValue() as any
    const initial = this.initialFormValue() as any
    const patch: any = {}

    // Include id when editing
    // const routeId = this.route.snapshot.params['id']
    const routeId = this.queryDepot.data()?.id
    if (routeId) patch.id = Number(routeId)

    // Always include required fields
    patch.name = current.name
    patch.emailIn = current.emailIn

    // Optional fields – include only when changed against initial snapshot
    const norm = (v: any) => (v === undefined || v === null ? '' : v)
    const idArray = (arr: any[]) =>
      Array.isArray(arr) ? arr.map((x: any) => x?.id).filter((v: any) => v !== undefined && v !== null) : []
    const eqArrayIds = (a: any[], b: any[]) => {
      const aa = idArray(a)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      const bb = idArray(b)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }

    if (norm(current.name) !== norm(initial?.name)) {
      patch.name = current.name
    }
    if (norm(current.emailIn) !== norm(initial?.emailIn)) {
      patch.emailIn = current.emailIn
    }

    return patch
  }

  public fillFormValue(depot) {
    const value = {
      name: depot.name ?? '',
      emailIn: depot.emailIn ?? '',
      // type: tag.type ?? '',
      // color: tag.color ?? '',
      // externalId: tag.externalId ?? '',
    }

    this.depotForm.setValue(value)
    // Store initial values snapshot and reset form dirty state
    this.initialFormValue.set(structuredClone(value))
    this.depotForm.markAsPristine()
  }

  private removeDepotListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['depots'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }
}
